import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import Nav, { WHATSAPP_ORDER_URL } from "../components/Nav";
import CouponStrip from "../components/CouponStrip";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import PosterDisplay from "../components/PosterDisplay";
import { getPricing } from "../lib/api";

const IMG = {
  hero: "https://images.unsplash.com/photo-1639690222869-1e608aa51f82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMGNsZWFuJTIwZm9sZGVkJTIwd2hpdGUlMjBsaW5lbnxlbnwwfHx8fDE3ODMxNjEwODJ8MA&ixlib=rb-4.1.0&q=85",
  wash: "https://images.unsplash.com/photo-1605459437907-541c4e2c0ed1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxmcmVzaCUyMGNsZWFuJTIwZm9sZGVkJTIwd2hpdGUlMjBsaW5lbnxlbnwwfHx8fDE3ODMxNjEwODJ8MA&ixlib=rb-4.1.0&q=85",
  dry: "https://images.unsplash.com/photo-1549040634-41fbcd2eb623",
  shoes: "https://images.pexels.com/photos/12628400/pexels-photo-12628400.jpeg",
  iron: "https://images.unsplash.com/photo-1489274495757-95c7c837b101?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxpcm9uaW5nJTIwY2xvdGhlcyUyMHByZXNzaW5nfGVufDB8fHx8MTc4MzE2MTA4Mnww&ixlib=rb-4.1.0&q=85",
};

// ---------- Motion primitives ----------
const EASE = [0.22, 1, 0.36, 1];

const revealUp = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: EASE } },
};

const stagger = (delay = 0) => ({
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: delay } },
});

// Kinetic word-by-word reveal
function KineticText({ text, className = "", accentIndexes = [] }) {
  const words = text.split(" ");
  return (
    <motion.span
      className={className}
      variants={stagger(0.05)}
      initial="hidden"
      animate="show"
      aria-label={text}
    >
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom">
          <motion.span
            className={`inline-block ${accentIndexes.includes(i) ? "italic font-normal text-rw-blue" : ""}`}
            variants={{
              hidden: { y: "110%", opacity: 0 },
              show: { y: 0, opacity: 1, transition: { duration: 0.85, ease: EASE } },
            }}
          >
            {w}
            {i < words.length - 1 && "\u00A0"}
          </motion.span>
        </span>
      ))}
    </motion.span>
  );
}

// Reveal-on-scroll wrapper
function Reveal({ children, delay = 0, y = 32, className = "" }) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

function Marquee() {
  const items = [
    "48-HOUR TURNAROUND",
    "FREE PICKUP OVER ₹299",
    "HYGIENIC & MACHINE-DRIED",
    "GURUGRAM · DLF · SOHNA ROAD",
    "SNEAKER & LEATHER CARE",
    "10,000+ CLOTHES WASHED",
  ];
  const doubled = [...items, ...items];
  return (
    <div className="border-y border-rw-ink/10 bg-rw-bg2 overflow-hidden">
      <div className="marquee-track flex gap-12 py-4 whitespace-nowrap">
        {doubled.map((t, i) => (
          <span key={i} className="font-display text-sm tracking-[0.25em] text-rw-ink2 flex items-center gap-12">
            {t}
            <span className="w-1.5 h-1.5 rounded-full bg-rw-blue" />
          </span>
        ))}
      </div>
    </div>
  );
}

function Hero() {
  const heroRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], reduce ? [1, 1] : [1.05, 1.15]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.3]);

  return (
    <section ref={heroRef} className="relative">
      <motion.div
        style={{ opacity: heroOpacity }}
        className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-8 md:pt-24 md:pb-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end"
      >
        <div className="md:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="overline mb-4"
          >
            01 · Doorstep laundry, done properly
          </motion.div>
          <h1
            className="font-display font-light tracking-tight text-rw-ink leading-[0.98] text-5xl sm:text-6xl lg:text-[6.5rem]"
            data-testid="hero-heading"
          >
            <KineticText text="Cleaner clothes," />
            <br />
            <KineticText text="calmer weekends." accentIndexes={[0]} />
          </h1>
          <motion.p
            variants={revealUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.55 }}
            className="mt-6 md:mt-8 text-base md:text-xl text-rw-ink2 max-w-xl leading-relaxed"
          >
            RIZ N WASH picks up your laundry, dry cleans it with care, and delivers it folded — all over
            Gurugram and all over Delhi NCR.
          </motion.p>
          <motion.div
            variants={revealUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.7 }}
            className="mt-6 md:mt-10 flex flex-wrap gap-3 items-center"
          >
            <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}>
              <a
                href={WHATSAPP_ORDER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-dark rounded-full px-6 md:px-7 py-3 md:py-3.5 text-sm md:text-base font-semibold inline-flex items-center gap-2 group"
                data-testid="hero-book-btn"
              >
                Order on WhatsApp
                <motion.span
                  className="inline-block"
                  animate={{ x: [0, 4, 0] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                >
                  →
                </motion.span>
              </a>
            </motion.div>
            <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}>
              <Link
                to="/track"
                className="rounded-full px-5 md:px-6 py-3 md:py-3.5 text-sm md:text-base font-semibold border border-rw-ink/20 text-rw-ink hover:bg-rw-ink hover:text-white transition-colors"
                data-testid="hero-track-btn"
              >
                Track my order
              </Link>
            </motion.div>
          </motion.div>
          <motion.div
            variants={stagger(0.85)}
            initial="hidden"
            animate="show"
            className="mt-8 md:mt-10 flex flex-wrap gap-6 md:gap-8 items-center text-xs md:text-sm text-rw-ink2"
          >
            {[
              ["48h", "turnaround"],
              ["10k+", "clothes washed"],
              ["4.9★", "avg rating"],
            ].map(([big, small]) => (
              <motion.div key={big} variants={revealUp} className="flex items-baseline gap-2">
                <span className="font-display text-xl md:text-2xl text-rw-ink">{big}</span>
                <span>·</span>
                <span>{small}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>

        <div className="md:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.2 }}
            className="relative rounded-2xl overflow-hidden border border-rw-ink/10 aspect-[4/5] md:aspect-[3/4]"
          >
            <motion.img
              src={IMG.hero}
              alt="Fresh clean folded linens"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-full object-cover will-change-transform"
            />
            <motion.div
              initial={{ y: 18, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.05, duration: 0.7, ease: EASE }}
              className="absolute bottom-4 left-4 right-4 bg-rw-bg2/90 backdrop-blur rounded-xl p-4"
            >
              <div className="overline mb-1">Next available slot</div>
              <div className="font-display text-lg md:text-xl">Today · 4:00 – 6:00 PM</div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}

function ServicesBento() {
  const cards = [
    { key: "wash", title: "Wash & Iron", tag: "Everyday", copy: "Fresh, machine-washed and neatly ironed. Perfect for weekly loads.", img: IMG.wash, className: "md:col-span-2 md:row-span-2 min-h-[420px]" },
    { key: "iron", title: "Iron / Pressing", tag: "Crisp finish", copy: "Shirts, sarees, kurtas — pressed with precision.", img: IMG.iron, className: "md:col-span-2 min-h-[220px]" },
    { key: "premium", title: "Premium Care", tag: "White-glove", copy: "Delicate, embroidered & designer garments handled with extra care.", img: IMG.shoes, className: "md:col-span-1 min-h-[220px]" },
    { key: "dry", title: "Dry Clean", tag: "Delicates", copy: "Suits, silks & sequins in expert hands.", img: IMG.dry, className: "md:col-span-1 min-h-[220px]" },
  ];
  return (
    <section id="services" className="max-w-7xl mx-auto px-6 md:px-10 py-24">
      <Reveal>
        <div className="grid md:grid-cols-12 gap-6 items-end mb-12">
          <div className="md:col-span-7">
            <div className="overline mb-3">04 Services</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-rw-ink leading-[1.05]">
              The way your clothes <em className="italic font-normal text-rw-blue">deserve</em> to be treated.
            </h2>
          </div>
          <p className="md:col-span-5 text-rw-ink2 text-lg leading-relaxed">
            Whatever the fabric, whatever the fuss — we&apos;ve built a workflow that keeps your wardrobe looking new,
            load after load.
          </p>
        </div>
      </Reveal>
      <motion.div
        className="grid grid-cols-1 md:grid-cols-4 gap-4"
        variants={stagger(0)}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-60px" }}
      >
        {cards.map((c) => (
          <motion.div
            key={c.key}
            variants={{
              hidden: { opacity: 0, y: 40 },
              show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE } },
            }}
            whileHover={{ y: -4 }}
            className={`group relative overflow-hidden rounded-2xl border border-rw-ink/10 bg-rw-bg2 ${c.className}`}
            data-testid={`service-card-${c.key}`}
          >
            <img
              src={c.img}
              alt={c.title}
              className="absolute inset-0 w-full h-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-rw-ink/70 via-rw-ink/10 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-6 text-white">
              <div className="overline text-white/70">{c.tag}</div>
              <h3 className="font-display text-2xl md:text-3xl mt-1">{c.title}</h3>
              <p className="text-sm text-white/80 mt-2 max-w-sm">{c.copy}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}

function PricingMenu({ pricing }) {
  return (
    <section id="pricing" className="bg-rw-bg2 border-y border-rw-ink/10">
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-24">
        <Reveal>
          <div className="text-center mb-12">
            <div className="overline mb-3">The Menu</div>
            <h2 className="font-display text-4xl md:text-6xl font-light tracking-tight">
              Honest pricing.<br className="hidden md:block" /> No surprises.
            </h2>
          </div>
        </Reveal>
        <motion.div
          className="divide-y divide-rw-ink/10"
          variants={stagger(0)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-40px" }}
        >
          {pricing.map((p, i) => (
            <motion.div
              key={p.id}
              variants={{
                hidden: { opacity: 0, y: 18 },
                show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
              }}
              className="flex items-baseline gap-3 py-6 group"
              data-testid={`pricing-row-${p.id}`}
            >
              <div className="w-8 font-display text-rw-ink3">{String(i + 1).padStart(2, "0")}</div>
              <div className="flex-1">
                <div className="flex items-baseline gap-3">
                  <h3 className="font-display text-2xl md:text-3xl text-rw-ink group-hover:text-rw-blue transition-colors">
                    {p.name}
                  </h3>
                  <span className="dotted-leader" />
                  <div className="font-display text-2xl md:text-3xl text-rw-ink" data-testid={`pricing-price-${p.id}`}>
                    {p.original_price > p.price && (
                      <span className="line-through text-rw-ink3 text-base md:text-lg mr-2 align-middle">₹{p.original_price}</span>
                    )}
                    ₹{p.price}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-rw-ink2">
                  <span>{p.description}</span>
                  <span className="w-1 h-1 rounded-full bg-rw-ink3" />
                  <span>{p.unit}</span>
                  <span className="w-1 h-1 rounded-full bg-rw-ink3" />
                  <span>{p.turnaround}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap items-center justify-between gap-4 text-sm text-rw-ink2">
            <p>* Minimum order ₹149. Free pickup & delivery on orders above ₹299.</p>
            <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}>
              <a href={WHATSAPP_ORDER_URL} target="_blank" rel="noopener noreferrer" className="btn-dark rounded-full px-5 py-2.5 font-semibold" data-testid="pricing-book-btn">
                Order on WhatsApp →
              </a>
            </motion.div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", t: "Schedule online", d: "Pick a slot, tell us what needs cleaning." },
    { n: "02", t: "We pick up", d: "Our runner arrives at your door — no charge." },
    { n: "03", t: "Care & clean", d: "Sorted, washed, ironed, quality-checked." },
    { n: "04", t: "Delivered fresh", d: "Folded, packed & handed back to you." },
  ];
  return (
    <section id="how" className="max-w-7xl mx-auto px-6 md:px-10 py-24">
      <Reveal>
        <div className="grid md:grid-cols-12 gap-6 mb-12">
          <div className="md:col-span-6">
            <div className="overline mb-3">How it works</div>
            <h2 className="font-display text-4xl md:text-6xl font-light tracking-tight leading-[1.05]">
              Four steps.<br />Zero laundry days.
            </h2>
          </div>
        </div>
      </Reveal>
      <motion.div
        className="grid grid-cols-1 md:grid-cols-4 gap-0 border-t border-rw-ink/10"
        variants={stagger(0)}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-60px" }}
      >
        {steps.map((s, i) => (
          <motion.div
            key={s.n}
            variants={{
              hidden: { opacity: 0, y: 24 },
              show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: EASE } },
            }}
            whileHover={{ backgroundColor: "rgba(146, 180, 210, 0.06)" }}
            className={`p-8 border-b border-rw-ink/10 ${i < 3 ? "md:border-r" : ""} border-rw-ink/10`}
            data-testid={`how-step-${s.n}`}
          >
            <div className="font-display text-5xl text-rw-blue mb-4">{s.n}</div>
            <h3 className="font-display text-xl mb-2">{s.t}</h3>
            <p className="text-rw-ink2 text-sm">{s.d}</p>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}

function Testimonials() {
  const items = [
    { n: "Ananya · Sector 43", t: "Best call I made all monsoon. Pickup was on time, clothes came back smelling amazing." },
    { n: "Rohit · DLF Phase 3", t: "My sneakers looked new. Genuinely surprised." },
    { n: "Priya · Sushant Lok", t: "I've switched to RIZ N WASH permanently. The ironing is chef's kiss." },
  ];
  return (
    <section className="bg-rw-ink text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <Reveal>
          <div className="overline text-white/60 mb-3">Word on the street</div>
          <h2 className="font-display text-4xl md:text-6xl font-light tracking-tight leading-[1.05] max-w-4xl">
            &ldquo;Cleaner clothes, calmer weekends.&rdquo;
          </h2>
        </Reveal>
        <motion.div
          className="grid md:grid-cols-3 gap-6 mt-14"
          variants={stagger(0)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
        >
          {items.map((it, i) => (
            <motion.div
              key={i}
              variants={{
                hidden: { opacity: 0, y: 24 },
                show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE } },
              }}
              whileHover={{ y: -6, borderColor: "rgba(146, 180, 210, 0.5)" }}
              className="border border-white/15 rounded-2xl p-6"
              data-testid={`testimonial-${i}`}
            >
              <div className="font-display text-2xl leading-snug">&ldquo;{it.t}&rdquo;</div>
              <div className="mt-6 overline text-white/60">{it.n}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function CtaBanner() {
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
      <Reveal>
        <motion.div
          whileHover={{ boxShadow: "0 20px 60px -30px rgba(26, 26, 36, 0.3)" }}
          transition={{ duration: 0.4 }}
          className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-10 md:p-16 grid md:grid-cols-12 gap-8 items-center"
        >
          <div className="md:col-span-8">
            <div className="overline mb-3">Ready when you are</div>
            <h3 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Skip laundry day.<br /> We&apos;ll be there in 30 minutes.
            </h3>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}>
              <a href={WHATSAPP_ORDER_URL} target="_blank" rel="noopener noreferrer" className="btn-dark rounded-full px-6 py-3 font-semibold text-base" data-testid="cta-banner-book">
                Order on WhatsApp →
              </a>
            </motion.div>
          </div>
        </motion.div>
      </Reveal>
    </section>
  );
}

export default function Home() {
  const [pricing, setPricing] = useState([]);

  useEffect(() => {
    getPricing().then((d) => setPricing(d.services || [])).catch(() => setPricing([]));
    // If arriving via a shared referral link (/?ref=CODE), stash it for the /book step
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref) localStorage.setItem("rw_pending_ref", ref.toUpperCase());
    } catch { /* noop */ }
  }, []);

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid="home-page">
      <Seo
        titleOverride="Doorstep Laundry & Dry Cleaning in Gurugram | RIZ N WASH"
        description="RIZ N WASH: Gurugram's premium doorstep laundry & dry cleaning service. Wash & fold from ₹19/piece, ironing ₹5/piece, dry cleaning from ₹149/piece and shoe/bag cleaning. Free pickup & delivery on orders above ₹299, 48-hour turnaround."
        path="/"
      />
      <Nav />
      <PosterDisplay slots={["banner", "popup"]} />

      <Hero />
      <CouponStrip />

      <Marquee />

      <PosterDisplay slots={["carousel"]} />

      <ServicesBento />
      <PricingMenu pricing={pricing} />
      <HowItWorks />
      <Testimonials />

      {/* Weekly plans teaser */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24" data-testid="home-plans-teaser">
        <Reveal>
          <div className="rounded-2xl overflow-hidden border border-rw-ink/10 bg-rw-bg2 p-10 md:p-16 grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <div className="overline mb-3">New · Weekly plans</div>
              <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight leading-[1.05]">
                Skip laundry, <em className="italic text-rw-blue not-italic">forever</em>.
              </h2>
              <p className="mt-4 text-rw-ink2 max-w-lg">
                Subscribe once, we come every Sunday. Save up to 20% vs pay-as-you-go.
              </p>
              <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }} className="inline-block">
                <Link to="/plans" className="btn-dark inline-flex mt-8 rounded-full px-6 py-3 font-semibold" data-testid="home-plans-btn">
                  See weekly plans →
                </Link>
              </motion.div>
            </div>
            <motion.div
              className="md:col-span-5 grid grid-cols-3 gap-2 text-center"
              variants={stagger(0.2)}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              {["Basic ₹349", "Family ₹649", "Premium ₹1199"].map((p, i) => (
                <motion.div
                  key={i}
                  variants={revealUp}
                  whileHover={{ y: -3, borderColor: "rgba(146, 180, 210, 0.6)" }}
                  className="rounded-xl border border-rw-ink/10 py-6 font-display"
                >
                  {p}
                  <div className="text-xs text-rw-ink3 font-body mt-1">/ week</div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </Reveal>
      </section>

      {/* Referral banner */}
      <section className="bg-rw-blue" data-testid="home-referral-banner">
        <Reveal>
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 flex flex-wrap items-center justify-between gap-6">
            <div>
              <div className="overline text-rw-ink/70 mb-2">Refer &amp; earn</div>
              <h2 className="font-display text-3xl md:text-4xl tracking-tight text-rw-ink max-w-2xl">
                Give friends 15% off. Get ₹100 credit for every one who books.
              </h2>
            </div>
            <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}>
              <a href={WHATSAPP_ORDER_URL} target="_blank" rel="noopener noreferrer" className="btn-dark rounded-full px-6 py-3 font-semibold" data-testid="home-referral-cta">
                Order on WhatsApp &amp; get your code →
              </a>
            </motion.div>
          </div>
        </Reveal>
      </section>

      <CtaBanner />

      {/* Brand reinforcement — helps disambiguate "riznwash" for search engines */}
      <section className="border-t border-rw-ink/10 bg-rw-bg" data-testid="home-about-brand">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <Reveal>
            <div className="overline mb-3">About the brand</div>
            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight leading-[1.1] mb-6">
              <span className="text-rw-ink">RIZ N WASH</span> — Gurugram's doorstep laundry &amp; dry cleaning brand.
            </h2>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="grid md:grid-cols-2 gap-8 text-rw-ink2 leading-relaxed">
              <p>
                <strong className="text-rw-ink">RIZ N WASH</strong> (spelled R-I-Z N W-A-S-H, sometimes written{" "}
                <em>Riz N Wash</em> or <em>riznwash</em>) is an owner-operated laundry &amp; dry cleaning service based in
                Signature Solera, Sector 107, Gurugram. We handle wash &amp; fold, ironing, dry cleaning, sneaker care and household
                textiles — with free pickup and 48-hour turnaround, available all over Gurugram and all over Delhi NCR.
              </p>
              <p>
                We are a <strong className="text-rw-ink">laundry brand</strong>, not a skincare or face-wash product.
                If you searched for “riznwash” looking for pickup laundry in Gurugram, you're in the right place.{" "}
                <Link to="/brand" className="underline text-rw-blue">Read the full brand story →</Link>
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
